import React, { useCallback, useEffect, useState } from 'react'
import { pick } from 'ramda'
import DataKeys from 'k8s/DataKeys'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import {
  listMngmGroups,
  deleteMngmGroup,
} from 'account/components/ssoManagement/groups/new-actions'
import { groupsSelector } from 'account/components/ssoManagement/groups/selectors'
import { routeManager } from 'core/utils/routes'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { ArrayElement } from 'core/actions/Action'
import { GridViewColumn } from 'core/elements/grid/Grid'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { loadSsoConfig } from '../sso/actions'
import AddGroupPage from 'app/plugins/account/components/ssoManagement/groups/AddGroupPage'
import EditGroupPage from 'app/plugins/account/components/ssoManagement/groups/EditGroupPage'

type ModelDataKey = DataKeys.ManagementGroups
type SelectorModel = ArrayElement<ReturnType<typeof groupsSelector>>

const usePrefParams = createUsePrefParamsHook<TablePrefsParams>('Groups', listTablePrefs)
const searchTargets = ['name', 'description', 'samlAttributesString']

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'name', label: 'Group Name' },
  { key: 'id', label: 'UUID', display: false },
  { key: 'description', label: 'Description' },
  { key: 'samlAttributesString', label: 'SAML Attributes Mapped' },
]

export default function GroupsListPage({ isSSOEnabled }) {
  const { params, getParamsUpdater } = usePrefParams({})
  const { message, loading, reload } = useListAction(listMngmGroups)
  const [ssoEnabled, setSsoEnabled] = useState(isSSOEnabled)
  const [loadingSso, setLoadingSso] = useState(!isSSOEnabled)
  const data = useSelector(groupsSelector)

  const loadingSomething = loading || loadingSso

  // Check if SSO is enabled only if it's not already known
  useEffect(() => {
    const getSettings = async () => {
      try {
        await loadSsoConfig()
        setSsoEnabled(true)
      } catch (err) {
        setSsoEnabled(false)
      }
      setLoadingSso(false)
    }

    if (!isSSOEnabled) getSettings()
  }, [])

  return (
    <>
      <DocumentMeta title="Groups" />
      <AddGroupPage />
      <EditGroupPage />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ManagementGroups}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loadingSomething}
        loadingMessage={message}
        onRefresh={reload}
        columns={columns}
        data={data}
        getParamsUpdater={getParamsUpdater}
        addUrl={routeManager.routes.sso.addGroup.path()}
        addText="New Group"
        addCond={() => ssoEnabled}
        editUrl={(_, id) => routeManager.routes.sso.editGroup.path({ id })}
        deleteAction={deleteMngmGroup}
        showBreadcrumbs={false}
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
